/**
 * Cart API Plugin
 * Provides $cartApi service for cart API operations
 */
import cartService from '~/services/cart';

export default ({ $axios, store, $cookies }, inject) => {
  const cartApi = cartService($axios, store);

  /**
   * Helper to update cart in store after API response
   * @param {Object} data - API response data containing cart and cart_id
   */
  const updateCartInStore = (data) => {
    if (data.cart) {
      store.commit('cart', data.cart);
      // Set cart_id cookie if provided
      if (data.cart_id || data.cart?.id) {
        const cartId = data.cart_id || data.cart.id;
        const domain = store.getters.store?.eshop?.url 
          ? `.${store.getters.store.eshop.url}` 
          : undefined;
        $cookies.set('cart_id', cartId, { domain });
      }
      // Emit cart update event
      if (typeof $nuxt !== 'undefined') {
        $nuxt.$emit('updateCart');
      }
    }
    return data;
  };

  // Wrap cart service methods with store update logic
  const wrappedCartApi = {
    /**
     * Add product to cart
     * @param {string} barcode - Product barcode
     * @param {number} quantity - Quantity to add (default: 1)
     * @param {Array} variation - Product variations (optional)
     * @returns {Promise<Object>} Cart response data
     */
    async addItem(barcode, quantity = 1, variation = []) {
      const data = await cartApi.addItem(barcode, quantity, variation);
      return updateCartInStore(data);
    },

    /**
     * Increase item quantity
     * @param {string} barcode - Product barcode
     * @param {number} amount - Amount to increase (default: 1)
     * @returns {Promise<Object>} Cart response data
     */
    async increaseQuantity(barcode, amount = 1) {
      const data = await cartApi.increaseQuantity(barcode, amount);
      return updateCartInStore(data);
    },

    /**
     * Decrease item quantity
     * @param {string} barcode - Product barcode
     * @param {number} amount - Amount to decrease (default: 1)
     * @param {boolean} removeOnZero - Remove item when quantity reaches 0 (default: true)
     * @returns {Promise<Object>} Cart response data
     */
    async decreaseQuantity(barcode, amount = 1, removeOnZero = true) {
      const data = await cartApi.decreaseQuantity(barcode, amount, removeOnZero);
      return updateCartInStore(data);
    },

    /**
     * Remove item from cart
     * @param {string} barcode - Product barcode
     * @returns {Promise<Object>} Cart response data
     */
    async removeItem(barcode) {
      const data = await cartApi.removeItem(barcode);
      return updateCartInStore(data);
    },

    /**
     * Update specific cart path
     * @param {string} path - Path to update (e.g., 'shipping.address', 'user.phone')
     * @param {any} value - Value to set
     * @returns {Promise<Object>} Cart response data
     */
    async updatePath(path, value) {
      const data = await cartApi.updatePath(path, value);
      return updateCartInStore(data);
    },

    /**
     * Bulk update multiple cart paths
     * @param {Array<{path: string, value: any}>} updates - Array of updates
     * @returns {Promise<Object>} Cart response data
     */
    async updatePaths(updates) {
      const data = await cartApi.updatePaths(updates);
      return updateCartInStore(data);
    },

    // Expose raw service for direct API access without auto-commit
    raw: cartApi
  };

  inject('cartApi', wrappedCartApi);
};
